import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { ToastController } from "@ionic/angular";
import { ReplaySubject, of, shareReplay, tap } from "rxjs";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ExpiredToken, TokenData } from "../models/tokenData";
import { User } from "../models/user";
import { PushService } from "./push.service";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  toastController = inject(ToastController);
  private push = inject(PushService);

  private _loggedIn: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private tokenData: TokenData;
  private userData: User;

  /** Troca de token em voo — ver refreshToken(). */
  private refresh$?: Observable<boolean>;

  doLogin(model: Record<string, unknown>) {
    this._loggedIn.next(true);
    const url = environment.api.baseUrl + environment.api.endpoints.login;
    return this.http
      .post<TokenData>(url, model)
      .toPromise()
      .catch(function (error) {
        throw error;
      });
    // return this.http.post<TokenData>(url, model).subscribe({
    //     next: (data) => {
    //         this.tokenData = data;
    //         if (this.tokenData.token_type == 'Bearer') {
    //             localStorage.setItem('authenticated', 'true');
    //             localStorage.setItem('tokenData', JSON.stringify(this.tokenData));
    //             this.setLoggerUser();
    //             this.profileService.setProfile();
    //         }
    //         this.router.navigate(['/main/home']);
    //     },
    //     error: (httpErrorResponse) => {
    //       //console.log(httpErrorResponse);
    //       this.errorToast(httpErrorResponse.error.error_description);
    //     }
    // });
  }

  async errorToast(errorMessage: any) {
    const toast = await this.toastController.create({
      message: errorMessage,
      duration: 2000,
      color: "danger",
    });
    toast.present();
  }

  setLoggerUser() {
    const url = environment.api.baseUrl + environment.api.endpoints.userLogged;
    return this.http
      .get<User>(url)
      .toPromise()
      .catch(function (error) {
        throw error;
      });

    // .subscribe({
    //   next: (data) => {
    //     let userData = { ...data };
    //     localStorage.setItem('currentAVUser', JSON.stringify(userData));
    //     userData = JSON.parse(localStorage.getItem('currentAVUser'));
    //   },
    //   error: (httpErrorResponse) => {
    //     this.errorToast(httpErrorResponse.error.error_description);
    //   }
    // });
  }

  update(id: any, model: any) {
    const url =
      environment.api.baseUrl +
      environment.api.endpoints.update_user +
      id +
      "/";
    return this.http.put<User>(url, model);
  }

  getLoggedUser() {
    this.userData = JSON.parse(localStorage.getItem("currentAVUser"));
    return this.userData;
  }

  isLoggedIn() {
    if (localStorage.getItem("authenticated") === "true") {
      this._loggedIn.next(true);
      return true;
    }
    this._loggedIn.next(false);
    return false;
  }

  getTokenData() {
    const jsonRaw = localStorage.getItem("tokenData");
    this.tokenData = JSON.parse(jsonRaw);
    return this.tokenData;
  }

  /** Unico ponto que grava o par de tokens. */
  setTokenData(tokenData: TokenData): void {
    this.tokenData = tokenData;
    localStorage.setItem("authenticated", "true");
    localStorage.setItem("tokenData", JSON.stringify(tokenData));
  }

  /**
   * Troca o refresh_token por um par novo (mesmo endpoint OAuth2 do login).
   *
   * O backend rotaciona o refresh a cada troca e revoga o anterior na hora,
   * entao a chamada em voo e compartilhada: dois 401 simultaneos disparando
   * duas trocas queimariam o token da segunda e derrubariam a sessao.
   * Emite false quando nao ha o que renovar — quem chamou decide o logout.
   */
  refreshToken(): Observable<boolean> {
    if (this.refresh$) return this.refresh$;

    const refresh_token = this.getTokenData()?.refresh_token;
    if (!refresh_token) return of(false);

    const url = environment.api.baseUrl + environment.api.endpoints.login;
    const { client_id, client_secret } = environment.api.entity.login;

    this.refresh$ = this.http
      .post<TokenData>(url, {
        client_id,
        client_secret,
        grant_type: "refresh_token",
        refresh_token,
      })
      .pipe(
        map((data: TokenData) => {
          if (!data?.access_token) return false;
          this.setTokenData(data);
          return true;
        }),
        catchError(() => of(false)),
        tap(() => (this.refresh$ = undefined)),
        shareReplay(1),
      );

    return this.refresh$;
  }

  logout() {
    // Idempotente: o DELETE do push abaixo pode voltar 401 depois da sessao ja
    // ter sido limpa e o interceptor chamaria logout() de novo, em loop.
    if (localStorage.getItem("authenticated") !== "true") return;

    // Antes de limpar o localStorage: o token.interceptor monta o Authorization
    // a partir de tokenData, entao o DELETE precisa sair com a sessao ainda de pe.
    // Sem isto o device continua ativo no backend e segue recebendo os pushes
    // do usuario anterior ate o proximo login reassociar o token.
    // A revogacao vem depois porque mata o token que o DELETE usa. O par e lido
    // agora, antes do localStorage sumir sob o callback.
    const tokenData = this.getTokenData();
    const token = tokenData?.refresh_token || tokenData?.access_token;
    const hint = tokenData?.refresh_token ? "refresh_token" : "access_token";
    this.push
      .removeToken()
      .subscribe({ complete: () => this.revokeSession(token, hint) });

    this._loggedIn.next(false);
    localStorage.setItem("authenticated", "false");
    localStorage.removeItem("tokenData");
    localStorage.removeItem("currentAVUser");
    localStorage.removeItem("currentHasProfile");
    localStorage.removeItem("currentProfile");
  }

  /**
   * Derruba o token no servidor (RFC 7009). Sem isto o logout era so local: o
   * access_token seguia valido ate expirar, mesmo em aparelho perdido.
   * Best-effort — o usuario ja saiu localmente de qualquer forma.
   */
  private revokeSession(token: string | undefined, hint: string): void {
    if (!token) return;

    const url = environment.api.baseUrl + environment.api.endpoints.revokeToken;
    const { client_id, client_secret } = environment.api.entity.login;

    this.http
      .post(url, { client_id, client_secret, token, token_type_hint: hint })
      .subscribe({ next: () => {}, error: () => {} });
  }

  getUserLoggedInObs(): Observable<boolean> {
    return this._loggedIn.asObservable();
  }

  removeUser() {
    const url = environment.api.baseUrl + "api/user/delete-account/";
    return this.http.delete<User>(url);
  }

  verifyToken(): Observable<ExpiredToken> {
    const url = environment.api.baseUrl + environment.api.endpoints.expiredToken;
    return this.http.get<ExpiredToken>(url);
  }
}
